import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { useQuery } from '@tanstack/react-query';

import type { AccessGroup } from '@/api/auth';
import type { ApiError } from '@/api/http';
import { scheduleInterview, type ScheduledInterview } from '@/api/interviews';
import type { ConsiderationOnCard } from '@/api/people';
import { questionSetsQuery } from '@/api/questionSets';
import { queryKeyRoots } from '@/api/queryKeys';
import { staffQuery } from '@/api/users';
import { roleQuestionSetsQuery } from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { mayReadStaffDirectory } from '@/pages/Candidates/candidateAccess';
import { interviewUrl } from '@/pages/Interview/interviewUrls';
import { staffName } from '@/pages/Settings/staffName';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { NO_SET, orderedSetOptions, preFilledSet } from './setPreFill';

interface Props {
  consideration: ConsiderationOnCard;
  /** The session user's id — the **default** interviewer, and the fallback when the directory fails. */
  viewerId: number;
  /** The viewer's groups: the directory read is gated on them, not only its rendering (ADR 0016). */
  groups: AccessGroup[];
  onClose: () => void;
}

/**
 * **The one field this form has**, and it is optional: an empty value sends **no** `scheduled_at` at
 * all (`InterviewIn` defaults it to the server's *now*).
 *
 * The schema is the form type (`.claude/rules/frontend.md` → Forms). It is deliberately a **string**
 * rather than a `Date`: `<input type="datetime-local">` holds a string in the reader's own clock with
 * no zone on it, and turning that into a request value is one conversion done in one place below.
 *
 * ⚠️ **There is no *"that is not a date"* message, and its absence is a decision rather than an
 * omission.** `type="datetime-local"` carries the HTML value-sanitisation algorithm: anything that is
 * not a valid local date and time — a half-typed value, a pasted word — becomes the **empty string**
 * before any handler sees it, in a browser and in jsdom alike (measured both ways). So a client-side
 * refusal here would be copy no reader could ever reach and no test could ever fail on, which is the
 * *"a criterion nothing can fail"* shape this project has already paid for once. What the field can
 * hand over is therefore `''` or a parseable local date-time, and the two branches below are the whole
 * of it.
 */
const scheduleSchema = z.object({
  scheduled_at: z.string().trim(),
  /**
   * The interviewer, as the control holds it: a **string**, because that is what a Radix `Select`
   * value is, converted once at the request boundary below. It is never empty in practice — the form
   * defaults to the caller — and the schema does not pretend to validate the id against the
   * directory: the server owns that (four cases behind one 404, backend decision 6).
   */
  interviewer: z.string(),
});

type ScheduleForm = z.infer<typeof scheduleSchema>;

/**
 * `2026-08-05T14:30` → `2026-08-05T14:30:00+03:00` — **the recruiter's own value, with the offset it
 * was typed in.**
 *
 * ⚠️ **The offset is the whole point.** `<input type="datetime-local">` gives a wall-clock reading and
 * no zone, and `InterviewIn.scheduled_at` is a `date-time`: sent zoneless it would be read as whatever
 * the server's clock happens to be, so *"2 p.m."* would mean two different instants for two recruiters
 * in two offices. `getTimezoneOffset()` is asked **of that instant** rather than of *now*, so a
 * conversation typed on the far side of a DST change carries the offset that will be in force then.
 *
 * The local reading is kept verbatim (rather than converted to UTC through `toISOString()`) because
 * both halves are then true of the string: it names the same instant, and it still says *14:30*, which
 * is what the recruiter typed and what the interview row will read back to them.
 */
function isoWithOffset(local: string): string {
  const instant = new Date(local);
  const minutesEastOfUtc = -instant.getTimezoneOffset();
  const sign = minutesEastOfUtc < 0 ? '-' : '+';
  const magnitude = Math.abs(minutesEastOfUtc);
  const pad = (value: number) => String(Math.trunc(value)).padStart(2, '0');
  // `datetime-local` omits the seconds when they are zero; `date-time` wants them.
  const seconds = local.length === 16 ? `${local}:00` : local;

  return `${seconds}${sign}${pad(magnitude / 60)}:${pad(magnitude % 60)}`;
}

/**
 * §6.5's **Schedule an interview** — the act nothing in the product performed until TASK-081, and the
 * only thing that creates the screen §6.6 fills in.
 *
 * ⚠️ **ADR 0010: this is not a status control, and the interface must not read as one.** `INTERVIEWING`
 * is a **consequence** of scheduling — `POST /considerations/{id}/interviews/` moves a `NEW`
 * consideration through the log, in the same transaction — and *"there is no `POST
 * …/start-interviewing/`: that would be the status dropdown this ADR refuses, with extra steps"*. So
 * three things hold here and each is asserted: the body is `InterviewIn` and carries **no `status`**;
 * this control is **not** produced by `statusActions.ts`, which knows nothing about it; and the new
 * status is read from the **refetched card** rather than patched in from the `201`'s
 * `consideration_status`. The dialog says the move as a consequence — *"…moves this run to Interviewing
 * if it is still New"* — and offers nothing called *"start interviewing"*.
 *
 * ⚠️ **The interviewer is now a choice, and this docblock carried the sentence explaining why it could
 * not be for exactly one sprint.** It read: *"`interviewer_id` is a user id and no route in this API
 * lists users, so the only id this client can honestly send is `session.user.id`"* — true when written
 * (TASK-081), and the reason an account in the `Interviewer` group could be given nothing to do through
 * the interface at all. `GET /api/v1/users/` (backend TASK-090) is that route, so the sentence is gone
 * and a `Select` of the tenant's staff stands where it was, **defaulting to the caller** because
 * scheduling yourself is the common case and was the only case until today.
 *
 * ⚠️ **The directory is read only when the viewer's group may read it** (`mayReadStaffDirectory`, ADR
 * 0016) — the request is not issued otherwise, which is `VacancyFilter`'s rule and matters for the same
 * reason: a demotion without a re-login leaves a cached list readable by somebody who has just lost the
 * right. An `Interviewer` never reaches this dialog anyway (`ConsiderationBlock` gates the control on
 * `mayWriteAboutPerson`), so the gate here is the second line rather than the only one.
 *
 * ⚠️ **A failed directory read does not block scheduling.** The picker degrades to what shipped before
 * it — the caller as the interviewer — with a sentence saying the list is unavailable, because a
 * recruiter writing down a conversation that has already happened must not be stopped by a list they do
 * not strictly need. That is §8's rule applied to a *decoration on a write*, and it is the same choice
 * §6.6's header makes for the candidate's name.
 *
 * ⚠️ **A vacancy whose bank is empty or entirely archived still schedules** (backend TASK-028: a `201`
 * with **zero** scores), so the answer's `score_count` is read and said — *including* when it is zero,
 * which is the state §6.6's empty screen is the other half of. That is why the `201` is a step of this
 * dialog rather than an immediate redirect: a sentence rendered for the length of one navigation is a
 * sentence nobody reads, and the reader is about to open a form with no questions in it.
 *
 * ⚠️ **409 — the candidate's personal data was erased on request** (ADR 0009, feedback protection #6).
 * Scheduling is one of the six write paths the erasure closes, so the control is not rendered on an
 * anonymised card at all (`ConsiderationBlock`, the gate *Edit* uses). Reaching this 409 therefore means
 * the erasure landed while the dialog was open — an Admin working at the same moment — and the answer is
 * this screen's own sentence, never the server's English `detail` and never a code (§8, §11).
 */
export function ScheduleInterviewDialog({ consideration, viewerId, groups, onClose }: Props) {
  const { t } = useTranslation(['candidates', 'common', 'components']);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /**
   * The tenant's staff — asked for **only** when this viewer's group may read the directory, so an
   * `Interviewer` who reached this dialog would issue no request rather than collect a 403 (ADR 0016;
   * `mayReadStaffDirectory` carries the matrix row). No narrowing at all: a tenant's staff is a short
   * list and the default page is 50, so a search box inside a dialog with two fields would be a control
   * for a problem no tenant of this product has yet — and `queryKeys.staff` carries the query anyway, so
   * adding one later cannot poison the unfiltered entry.
   *
   * ⚠️ This paragraph read *"No `?search=`"*, as though the call were declining a parameter it could
   * have sent. **There is no such parameter** (BUG-048, 2026-08-05): it was removed from the route
   * because nothing in either repository ever passed it, and this call site was one of the two that did
   * not. It returns with the screen that needs it, in that screen's commit.
   */
  const staff = useQuery({ ...staffQuery(), enabled: mayReadStaffDirectory(groups) });

  /**
   * The options: every active account of the tenant, the caller labelled as themselves.
   *
   * ⚠️ **The caller is always in the list, even when the directory could not be read** — that is the
   * fallback that keeps the form usable, and it is also correct on a successful read, where the caller
   * is one row of their own tenant's staff like everybody else. The label carries the group when there
   * is one, because an account in **no** group can be assigned an interview and then cannot open it
   * (backend TASK-090's own note), and that is worth seeing at the moment of assignment.
   *
   * ⚠️ **All three labels are named through `staffName`, and this is the site that *manufactured* the
   * blank rather than passing one on** (BUG-041, the fourth site the entry did not list). The third
   * shape is built from parts — `name · groups` — so an account the server named blank offered an
   * option reading *"· Recruiter"*, measured in a live browser for `rec@acme.test`: a picker whose
   * options are a separator and a group name cannot be used to tell two colleagues apart, which is the
   * one thing it exists for.
   */
  const interviewerOptions = (() => {
    const rows = staff.data?.items ?? [];
    if (rows.length === 0) {
      return [{ value: String(viewerId), label: t('candidates:card.schedule.you') }];
    }

    return rows.map((person) => {
      const name = staffName(person, t('common:unnamedAccount'));
      return {
        value: String(person.id),
        label:
          person.id === viewerId
            ? t('candidates:card.schedule.youNamed', { name })
            : person.groups.length === 0
              ? t('candidates:card.schedule.noGroup', { name })
              : `${name} · ${person.groups.map((group) => t(`components:group.${group}`, group)).join(', ')}`,
      };
    });
  })();

  const form = useForm<ScheduleForm>({
    resolver: zodResolver(scheduleSchema),
    // Empty `scheduled_at`, and it stays empty unless somebody types: an untouched form sends no
    // `scheduled_at`, so the instant recorded is the **server's** clock. A client-computed "now" is the
    // client's clock, which `InterviewIn`'s own docstring names as the reason the field is optional.
    //
    // The interviewer defaults to **the caller** — the common case, and the only case before the
    // directory existed — so an untouched form behaves exactly as TASK-081's did.
    defaultValues: { scheduled_at: '', interviewer: String(viewerId) },
  });

  /** Who the form currently names — the pre-fill is **their** set, not the caller's. */
  const chosenInterviewer = Number.parseInt(form.watch('interviewer'), 10) || viewerId;

  /**
   * ⚠️ **The set the reader chose *by hand*, and `null` until they have — which is the whole of the
   * pre-fill mechanism.** The value on screen is **derived** below rather than stored: a state that
   * held the pre-filled value would need an effect to keep it in step with the interviewer, and that
   * effect is where two defects live. The first is ordinary — an effect that overwrites a deliberate
   * choice — and the second was **measured** while writing this (jsdom, `scheduleSet.test.tsx`):
   * setting a Radix `Select`'s controlled value *after* it has mounted, inside a `<form>`, made the
   * control emit `onValueChange('')` and land back on no value at all. Deriving it means the picker
   * mounts already holding the right value and never transitions at all.
   *
   * ⚠️ **It is component state and not a form field, deliberately.** `scheduled_at` needs the offset
   * conversion and `interviewer` needs a value the request is built from, so both live in the form and
   * `zod` types them; a set is either one of the ids this picker itself offered or `NO_SET`, so a
   * schema over it would be a rule the control cannot break.
   */
  const [chosenByHand, setChosenByHand] = useState<string | null>(null);

  /**
   * The three reads this field needs, and each answers a different question.
   *
   *  * **The chosen interviewer's own sets** (`?author_id=`) — ADR 0016 decision 5(b)'s filter, and
   *    the only reason it exists. An unknown, retired or foreign id answers an **empty page** rather
   *    than a 404, deliberately (a refusal would make it a cheaper account-existence oracle than the
   *    directory), so *"they have written none"* and *"that id is nobody"* are the same answer here —
   *    which is exactly right for a pre-fill.
   *  * **The vacancy's attached sets** — the fallback, and the labels on the options.
   *  * **The tenant's sets this reader may see** — the rest of the list, because the server refuses no
   *    set for not being attached (ADR 0018 decision 3).
   *
   * All three are keyed on the viewer (`queryKeys.questionSets`) because the set list is **narrowed**
   * per caller rather than refused.
   */
  const authoredSets = useQuery(
    questionSetsQuery(viewerId, { author_id: chosenInterviewer, page_size: 100 }),
  );
  const vacancySets = useQuery(roleQuestionSetsQuery(consideration.role_id));
  const tenantSets = useQuery(questionSetsQuery(viewerId, { page_size: 100 }));

  const setOptions = orderedSetOptions(vacancySets.data ?? [], tenantSets.data?.items ?? []);

  /**
   * ⚠️ **Whether this field is a choice at all.** With either list unread there is nothing to choose
   * from, so the control is not the reader's answer to *"which set?"* — it is a picker with one option
   * in it. That distinction decides what goes on the wire below, and the sentence beneath the field
   * says the same thing in words.
   */
  const setListUnavailable = tenantSets.isError || vacancySets.isError;

  /**
   * ⚠️ **Whether the workspace has more sets than this picker was handed** (`security-auditor` L8,
   * 2026-08-12). `?page_size=` is clamped to `NINJA_MAX_PER_PAGE_SIZE = 100`, so a bigger number is
   * not available and past 100 the picker would silently offer a subset of a list whose whole purpose
   * is choosing from everything the reader may see. It is knowable rather than guessable —
   * `PagedQuestionSetOut` sends `count` beside the page — so it is **said**, in §6.9's own shape: how
   * many arrived, out of how many there are, and the order they were taken in.
   *
   * ⚠️ **The vacancy's own list cannot truncate** and is deliberately not counted here: that route is
   * unpaginated on purpose (*"a page-2 offer is an offer that hides options"*). What can also run past
   * 100 is the **authored** read behind the pre-fill, and it is left alone: a reader with more than a
   * hundred sets of their own gets the lowest id among the first hundred by name, which is a pre-fill
   * they can change rather than an option they cannot reach.
   */
  const setsTruncated =
    tenantSets.data !== undefined && tenantSets.data.count > tenantSets.data.items.length;

  /**
   * ⚠️ **The pre-fill, re-computed whenever the chosen interviewer changes and never over a hand
   * choice.** It waits for both reads to settle — a pre-fill computed from a list still in flight
   * would flip under the reader's cursor — and it writes `NO_SET` when the rule produces nothing,
   * which is the honest state rather than an empty control.
   */
  const preFilled = preFilledSet(authoredSets.data?.items ?? [], vacancySets.data ?? []);
  const setsSettled = !authoredSets.isPending && !vacancySets.isPending;

  /**
   * What the picker holds: the reader's own choice if they have made one, and the pre-fill otherwise.
   * **Derived, so changing the interviewer re-computes it and a hand choice is never overwritten** —
   * the two halves of the criterion fall out of one expression rather than out of an effect that has
   * to remember which of them is which.
   */
  const questionSet = chosenByHand ?? (preFilled === null ? NO_SET : String(preFilled));

  const schedule = useMutation<ScheduledInterview, ApiError, ScheduleForm>({
    mutationFn: ({ scheduled_at, interviewer }) =>
      scheduleInterview(consideration.id, {
        /**
         * ⚠️ **The chosen interviewer, and the caller when the directory could not be read.** The
         * control holds a string (Radix's own value type) and the request takes a number, so the
         * conversion happens here, once. `Number.parseInt` rather than `Number(...)`: an unexpected
         * value becomes `NaN` rather than `0`, and `NaN` is refused by the schema before it leaves —
         * a `0` would be sent and answered 404, which reads as *"the candidate is gone"*.
         */
        interviewer_id: Number.parseInt(interviewer, 10) || viewerId,
        // Omitted rather than nulled when nothing was typed: `null` is not what "use your own clock"
        // is spelled as, and the key being absent is.
        ...(scheduled_at === '' ? {} : { scheduled_at: isoWithOffset(scheduled_at) }),
        /**
         * ⚠️ **The key's *presence* is the statement, and *No set* is `null` rather than an
         * omission** — the contract agreed with the backend in this sprint's review round, and a
         * correction: this dialog omitted the key for *No set*, the server read the omission as
         * *"use the vacancy's set"*, and an interview the reader had deliberately asked to build from
         * **no** set froze all of the vacancy's questions anyway. The three statements are now
         * distinct — an id names a set, `null` says *no set at all*, and an absent key says
         * *"decide for me"*.
         *
         * ⚠️ **The absent key still has exactly one caller, and it is not a choice.** When neither
         * list could be read (`setListUnavailable`) the field offers nothing to choose from, and the
         * sentence beneath it promises the vacancy's own set — so the honest body is the one that
         * asks the server to decide, byte-identical to what shipped before this field existed. A
         * `null` there would turn a failed **read** into an interview with no questions in it.
         */
        ...(setListUnavailable
          ? {}
          : { question_set_id: questionSet === NO_SET ? null : Number(questionSet) }),
      }),
    /**
     * ⚠️ **Three roots, and the answer is not written into any cache.** `InterviewCreatedOut` is
     * metadata plus a count: it carries neither the card's derived values nor §6.3's row nor §6.2's
     * outstanding count, so it is **read for its `score_count` and thrown away**, and the three
     * families that moved are refetched — the consideration's **status** and the person's
     * `interview_count` (`person`), §6.3's Status and Updated columns for this run
     * (`considerations`), and §6.2's outstanding-feedback count, which has just grown by one
     * (`dashboard`). Patching the status in from `consideration_status` would be this screen deciding
     * a status locally, which is the one thing ADR 0010 keeps on the server.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.considerations });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.dashboard });
    },
  });

  const submit = form.handleSubmit(async (submitted) => {
    try {
      await schedule.mutateAsync(submitted);
    } catch {
      // Held by the mutation and rendered below; rethrowing reaches only the console.
    }
  });

  const failure = (() => {
    if (!schedule.error) return null;
    // ⚠️ The **write** sentence, not §8's read banner (BUG-017): nothing queues this `POST`, and an
    // interview a recruiter believes was queued is one they will not arrange again.
    if (schedule.error.isOffline) return t('components:state.offlineWrite');
    if (schedule.error.isThrottled) return t('components:state.throttled');
    if (schedule.error.status === 409) return t('candidates:card.schedule.anonymized');
    /**
     * ⚠️ **A 404 covers two different things here and this sentence claims neither**: a consideration
     * outside this caller's reach, and an `interviewer_id` the server will not accept — another
     * tenant's user, a global superuser, a deactivated account, or an id that never existed (backend
     * decision 6, four cases behind one answer). Guessing which would be wrong four times out of five,
     * so what is said is what a reader can act on: nothing was created, and the card in front of them
     * may be out of date.
     */
    if (schedule.error.status === 404) return t('candidates:card.schedule.notFound');
    return t('candidates:card.schedule.failed');
  })();

  const created = schedule.data;
  /** The name of the set the server says it froze from, or nothing if it can name none. */
  const frozenSetName =
    setOptions.find(({ set }) => set.id === created?.question_set_id)?.set.name ?? null;
  /**
   * Whether the conversation just created is **this reader's own** — which decides whether there is
   * anything for them to open. `created.interviewer_id` is the server's answer rather than the value
   * the form sent, so a body the API adjusted (it does not today) could not make this claim wrong.
   */
  const assignedToMe = created?.interviewer_id === viewerId;
  /** The assignee's name for that answer — read off the option the reader picked. */
  const interviewerName =
    interviewerOptions.find((option) => option.value === String(created?.interviewer_id))?.label ??
    t('candidates:card.schedule.someoneElse');

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        created ? t('candidates:card.schedule.done.title') : t('candidates:card.schedule.title')
      }
      description={
        created
          ? undefined
          : t('candidates:card.schedule.description', { vacancy: consideration.role_title })
      }
      footer={
        created ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common:close')}
            </Button>
            {/*
             * ⚠️ **Offered only when the caller is the interviewer, and that is a live-pass finding
             * rather than a precaution** (TASK-095, 2026-07-31). This comment read *"the caller **is**
             * the interviewer, so the destination is their own draft and not a blinded state"* — true
             * while the caller was the only possible assignee, false the moment TASK-093 let them name
             * a colleague. Measured in a browser: a recruiter who assigned the conversation to somebody
             * else pressed this and landed on *"Hidden until you submit your own feedback."* — a button
             * promising a screen it cannot deliver, which is the shape §5 and BUG-020 both rule out.
             * When it went to a colleague there is nothing for this reader to open, and the answer says
             * who it went to instead.
             */}
            {assignedToMe && (
              <Button onClick={() => void navigate(interviewUrl(created.id))}>
                {t('candidates:card.schedule.done.open')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common:cancel')}
            </Button>
            <Button onClick={() => void submit()} isLoading={schedule.isPending}>
              {t('candidates:card.schedule.submit')}
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="flex flex-col gap-2">
          {/*
           * ⚠️ **The count is stated, and zero is stated too.** ADR 0004 freezes the vacancy's active
           * bank into this interview at this moment, and a bank that is empty or entirely archived
           * gives a valid interview with **no** questions (backend TASK-028). Hiding that would leave
           * the reader to discover an empty form and read it as a failure.
           */}
          <p className="text-sm text-ink-900">
            {created.score_count === 0
              ? t('candidates:card.schedule.done.noQuestions')
              : t('candidates:card.schedule.done.questions', { count: created.score_count })}
          </p>

          {/*
           * ⚠️ **Which set it was frozen from, read off the `201` rather than off the form.**
           * `InterviewCreatedOut.question_set_id` is the server's answer — so the omitted-set path,
           * where `_set_for` picked the vacancy's, names the set the reader never chose, which is the
           * one case where this sentence tells them something they did not already know. The name is
           * looked up in the options the picker offered; a set that is somehow not among them is
           * named by nothing rather than by an id, because *"an internal id anywhere a human can
           * see it"* is the one thing §6.5 has always refused.
           *
           * ⚠️ **It is also the sentence that replaced a false one.** §6.5 read *"7 questions were
           * frozen into this interview from this vacancy's bank as it stands now"* until 2026-08-12,
           * and a vacancy has had no bank since ADR 0018 — the questions come from a **set**. That
           * wording is **retracted** and `retractedClaims.test.ts` holds it.
           */}
          {frozenSetName !== null && (
            <p className={cn(type.body, 'text-ink-700')}>
              {t('candidates:card.schedule.done.fromSet', { name: frozenSetName })}
            </p>
          )}

          {/*
           * ⚠️ **Who it went to, when it did not go to this reader** — the other half of the finding
           * above. Without it the dialog would close on an act whose only visible trace is a new row
           * further down the card, and the reader would have no confirmation that the person they
           * chose is the person the server recorded.
           *
           * ⚠️ **The wording was wrong on its first draft and the live pass caught it.** It read
           * *"it will appear on their dashboard rather than yours"* — false for the reader most likely
           * to see it: §6.2 Section 1 shows a `Recruiter` and an `Admin` the **whole tenant's**
           * outstanding work, so the row appears on *their* dashboard too, attributed to the colleague
           * (measured: four rows, all attributed to `int@globex.test`, on the recruiter's own screen).
           * What is true is what it says now — the row is **theirs**, and only they can write it up.
           */}
          {!assignedToMe && (
            <p className={cn(type.body, 'text-ink-700')}>
              {t('candidates:card.schedule.done.assigned', { name: interviewerName })}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
            {/*
             * ⚠️ **The control that replaced a sentence.** Until TASK-093 this was a paragraph saying
             * the interviewer could only be the caller, because no route listed users. It defaults to
             * the caller still — the common case — and the reader may hand the conversation to anybody
             * active in their tenant.
             */}
            <Field label={t('candidates:card.schedule.interviewerLabel')}>
              {(fieldProps) =>
                staff.isPending && mayReadStaffDirectory(groups) ? (
                  /* §8's loading state for a control: the shape of the answer, never a spinner. */
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <Select
                    {...fieldProps}
                    value={form.watch('interviewer')}
                    onValueChange={(next) => form.setValue('interviewer', next)}
                    options={interviewerOptions}
                  />
                )
              }
            </Field>

            {/*
             * ⚠️ **A directory that could not be read does not stop a recruiter writing down a
             * conversation that already happened.** The picker falls back to the caller — which is what
             * shipped before the route existed — and says so; §8's error state over a control that has
             * a working default would cost the write for a decoration.
             */}
            {staff.isError && (
              <p className={cn(type.metadata, 'text-ink-700')}>
                {t('candidates:card.schedule.directoryUnavailable')}
              </p>
            )}

            {/*
             * ⚠️ **The set the interview freezes, chosen by whoever schedules it** — the user's
             * decision 1, taken 2026-08-12, and the last link in the chain from *"a question exists"*
             * to *"an interview asks it"*. Three things about it are decisions rather than detail:
             *
             *  * **It pre-fills the chosen interviewer's own set** (`setPreFill.ts`), re-computes when
             *    the interviewer changes, and never overwrites a set the scheduler picked by hand.
             *  * **Every set this reader may see is offered**, with the vacancy's first and labelled
             *    as such — the attachment is an **offer, not a rule** (ADR 0018 decision 3), and the
             *    server refuses no set for not being attached. A picker that hid the others would be
             *    this client inventing the funnel rule the product already refused once.
             *  * **The freeze is at creation and nothing here may imply otherwise** (ADR 0004,
             *    feedback protection #1). The set is named **now**, in this form, and no request after
             *    the `201` carries one — the alternative, letting the interviewer choose when they
             *    open the form, is recorded as **rejected**: the guarantee that a mid-interview edit,
             *    reorder or retirement cannot reshape the form somebody is sitting in would have to be
             *    re-derived for a form that does not exist until somebody looks at it.
             */}
            <Field label={t('candidates:card.schedule.setLabel')}>
              {(fieldProps) =>
                !setsSettled ? (
                  /* §8's loading state for a control: the shape of the answer, never a spinner. */
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <Select
                    {...fieldProps}
                    value={questionSet}
                    onValueChange={(next) => {
                      setChosenByHand(next);
                    }}
                    options={[
                      ...setOptions.map(({ set, offeredByThisVacancy }) => ({
                        value: String(set.id),
                        label: offeredByThisVacancy
                          ? t('candidates:card.schedule.setOffered', {
                              name: set.name,
                              count: set.active_question_count,
                            })
                          : t('candidates:card.schedule.setOther', {
                              name: set.name,
                              count: set.active_question_count,
                            }),
                      })),
                      /*
                       * ⚠️ **A real option rather than an empty control**, and it is last because it
                       * is the least likely choice. Without it a reader could not undo a pre-fill,
                       * and it is also the honest state when the workspace has no set at all: the
                       * interview is valid, it has no questions, and §6.6's empty screen says so.
                       */
                      { value: NO_SET, label: t('candidates:card.schedule.setNone') },
                    ]}
                  />
                )
              }
            </Field>

            {/*
             * ⚠️ **A list that could not be read does not block the write** — the same rule the
             * interviewer picker already has, and the same reason: a recruiter writing down a
             * conversation that has already happened must not be stopped by a list they do not
             * strictly need. The field degrades to a picker with nothing in it, the request carries
             * **no** `question_set_id` at all — the one place the key is still omitted, since here it
             * is not a reader's choice — and the server takes the vacancy's attached set exactly as
             * it always did, which is what this sentence promises.
             */}
            {setListUnavailable && (
              <p className={cn(type.metadata, 'text-ink-700')}>
                {t('candidates:card.schedule.setsUnavailable')}
              </p>
            )}

            {/* A workspace with more sets than one page holds says so, with both numbers. */}
            {setsTruncated && tenantSets.data !== undefined && (
              <p className={cn(type.metadata, 'text-ink-700')}>
                {t('candidates:card.schedule.setsPartial', {
                  count: tenantSets.data.count,
                  shown: tenantSets.data.items.length,
                })}
              </p>
            )}

            {/* No `error` prop: the control cannot produce an invalid value to complain about — see
                `scheduleSchema` above, where that is measured rather than assumed. */}
            <Field label={t('candidates:card.schedule.when')}>
              {(fieldProps) => (
                <Input type="datetime-local" {...fieldProps} {...form.register('scheduled_at')} />
              )}
            </Field>
            <p className="text-xs text-ink-500">{t('candidates:card.schedule.whenHint')}</p>
          </form>

          {/*
           * ⚠️ **ADR 0010's consequence, said as a consequence.** Not a choice, not a second control,
           * and never a button called *"start interviewing"* — the status moves because a conversation
           * now exists, and only from `NEW`.
           */}
          <p className="text-sm text-ink-700">{t('candidates:card.schedule.consequence')}</p>

          {failure && (
            <p role="alert" className="text-sm text-negative">
              {failure}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
